// グラフプレビューコンポーネント

import { useMemo } from 'react';
import type { ReactElement } from 'react';
import { graphRegistry } from './GraphRegistry';
import { GraphConfig, GraphCallbacks } from '../../../types/graph-builder.types';
import type { DataSet } from '../../../types/dataset.types';
import { applyFilterToDataset } from '../../utils/applyFilterToDataset';
import logger from '../../utils/logger';
import * as styles from '../../styles/components/graph-builder.css.ts';

interface GraphPreviewProps {
  graphType?: string;
  config?: GraphConfig;
  dataset?: DataSet;
  filterExpression?: string;
  callbacks: GraphCallbacks;
  validationError?: string;
}

export function GraphPreview({
  graphType,
  config,
  dataset,
  filterExpression,
  callbacks,
  validationError
}: GraphPreviewProps) {
  // フィルタ式がある場合は、データセットをフィルタリング
  const filteredDataset = useMemo(() => {
    if (!dataset || !filterExpression) {
      return dataset;
    }

    try {
      return applyFilterToDataset(dataset, filterExpression);
    } catch (error) {
      logger.warn('[GraphPreview] Failed to apply filter', { error });
      return dataset; // フィルタ失敗時は元データを使用
    }
  }, [dataset, filterExpression]);

  // グラフが設定されていない場合の表示
  if (!graphType || !config || !filteredDataset) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateIcon}>📊</div>
        <p className={styles.emptyStateText}>
          Select a graph type and dataset to begin
        </p>
      </div>
    );
  }

  const entry = graphRegistry.get(graphType);
  if (!entry) {
    return <div className={styles.errorMessage}>Unknown graph type</div>;
  }

  // 設定の検証
  const error = validationError || entry.renderer.validate(config, filteredDataset);

  // コンテンツをレンダリング
  let content: ReactElement;

  if (error) {
    content = <div className={styles.errorMessage}>{error}</div>;
  } else {
    // フィルタ情報の表示
    const filterInfo = filterExpression && dataset ? (
      <div className={styles.filterRowCount} data-testid="filter-row-count">
        Showing {filteredDataset.rowCount} of {dataset.rowCount} rows (filtered)
      </div>
    ) : null;

    // グラフをレンダリング
    try {
      const GraphComponent = entry.renderer.render(config, filteredDataset, callbacks);
      content = (
        <>
          {filterInfo}
          {GraphComponent}
        </>
      );
    } catch (renderError) {
      logger.error('[GraphPreview] Error rendering graph', renderError);
      content = <div className={styles.errorMessage}>Error rendering graph</div>;
    }
  }

  return content;
}